/**
 * /pricing — public page listing every active premium subscription tier.
 *
 * Auth-gated CTA: unsigned visitors are bounced to /login?next=/pricing.
 * Signed-in users hit `POST /api/premium/checkout` and are redirected to
 * Stripe Checkout.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Star, Check, Sparkles, Loader2 } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyTier, setBusyTier] = useState(null); // key: `${id}-${interval}`
  const [interval, setInterval] = useState("monthly");
  const [me, setMe] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [t, m] = await Promise.all([
          http.get("/premium/tiers"),
          user ? http.get("/premium/me").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        if (!mounted) return;
        setTiers(t.data.tiers || []);
        setMe(m?.data || null);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [user]);

  const subscribe = async (tier) => {
    if (!user) { nav(`/login?next=/pricing`); return; }
    const key = `${tier.id}-${interval}`;
    setBusyTier(key);
    try {
      const { data } = await http.post("/premium/checkout", {
        tier_id: tier.id,
        interval,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Couldn't create checkout session.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusyTier(null);
    }
  };

  return (
    <div data-testid="pricing-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
      <div className="text-center mb-10">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
          <Star size={12} className="inline -mt-0.5 mr-1" /> PREMIUM
        </div>
        <h1 className="font-display text-4xl md:text-5xl">Support the mission.</h1>
        <p className="mt-3 text-base max-w-xl mx-auto" style={{ color: "var(--wofy-text-soft)" }}>
          Choose a plan that fits how you want to contribute. Cancel anytime — every dollar keeps
          Social Pulse open, ad-light, and community-owned.
        </p>
        <div className="inline-flex items-center gap-1 mt-6 p-1 rounded-full border" style={{ borderColor: "var(--wofy-line)" }} data-testid="pricing-interval-toggle">
          {["monthly", "yearly"].map((k) => (
            <button
              key={k}
              onClick={() => setInterval(k)}
              className={`text-xs uppercase tracking-widest font-semibold px-4 py-1.5 rounded-full transition ${interval === k ? "" : "hover:opacity-70"}`}
              style={{
                background: interval === k ? "var(--wofy-primary)" : "transparent",
                color: interval === k ? "white" : "var(--wofy-text)",
              }}
              data-testid={`pricing-interval-${k}`}
            >
              {k === "yearly" ? "Yearly · save 2 months" : "Monthly"}
            </button>
          ))}
        </div>
      </div>

      {me?.is_premium && (
        <div
          className="mb-8 wofy-card border-2"
          style={{ borderColor: "var(--wofy-primary)", background: "linear-gradient(135deg,#FEF3C7,#FFE4E6)" }}
          data-testid="pricing-current"
        >
          <div className="flex items-center gap-2 font-semibold">
            <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
            You're already on <b>{me.tier_name}</b> ({me.interval}).
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Manage or cancel from your <Link to="/dashboard" className="underline">dashboard</Link>.
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="inline w-6 h-6 animate-spin" /></div>
      ) : tiers.length === 0 ? (
        <div className="wofy-card text-center py-16 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          Premium plans are coming soon. Check back shortly!
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiers.map((t) => {
            const isFree = !!t.is_free;
            const price = isFree ? "Free" : (interval === "yearly" ? t.price_display_yearly : t.price_display_monthly);
            const priceMissing = !isFree && (interval === "yearly" ? !t.price_display_yearly : !t.price_display_monthly);
            const isBusy = busyTier === `${t.id}-${interval}`;
            const alreadyOnThis = me?.is_premium && me.tier_id === t.id && me.interval === interval;
            return (
              <div
                key={t.id}
                className="wofy-card flex flex-col"
                data-testid={`pricing-tier-${t.id}`}
                style={{
                  borderColor: t.highlight ? "var(--wofy-primary)" : "var(--wofy-border)",
                  boxShadow: t.highlight ? "0 12px 32px rgba(178,93,56,0.15)" : undefined,
                  borderWidth: t.highlight ? 2 : 1,
                  transform: t.highlight ? "translateY(-4px)" : undefined,
                }}
              >
                {t.badge && (
                  <div className="absolute -mt-9 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full self-start" style={{ background: "var(--wofy-primary)", color: "white" }}>
                    {t.badge}
                  </div>
                )}
                <h3 className="font-display text-2xl">{t.name}</h3>
                {t.tagline && <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{t.tagline}</p>}
                <div className="mt-4 mb-2">
                  <span className="font-display text-4xl">{isFree ? "$0" : (priceMissing ? "—" : price)}</span>
                  {isFree ? (
                    <span className="text-xs ml-1" style={{ color: "var(--wofy-text-soft)" }}>forever</span>
                  ) : !priceMissing && (
                    <span className="text-xs ml-1" style={{ color: "var(--wofy-text-soft)" }}>
                      {interval === "yearly" ? "billed yearly" : "billed monthly"}
                    </span>
                  )}
                </div>
                {t.founding && t.founding_tagline && interval === "yearly" && !isFree && (
                  <div className="text-xs font-semibold rounded-lg px-2 py-1 mb-2" style={{ background: "rgba(178,93,56,0.1)", color: "var(--wofy-primary)" }} data-testid={`pricing-founding-${t.id}`}>
                    ★ {t.founding_tagline}
                  </div>
                )}
                {t.description && <p className="text-sm mt-1 mb-3">{t.description}</p>}
                <ul className="text-sm space-y-1.5 my-3">
                  {(t.perks || []).map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--wofy-primary)" }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                {isFree ? (
                  user ? (
                    <div className="mt-auto text-center text-sm py-2 rounded-full font-semibold" style={{ background: "rgba(22,163,74,0.1)", color: "#16a34a" }} data-testid={`pricing-free-${t.id}`}>
                      ✓ You're on this plan
                    </div>
                  ) : (
                    <Link
                      to="/register"
                      className={t.highlight ? "wofy-btn-primary mt-auto justify-center" : "wofy-btn-ghost mt-auto justify-center border"}
                      style={t.highlight ? {} : { borderColor: "var(--wofy-line)" }}
                      data-testid={`pricing-signup-${t.id}`}
                    >
                      Sign up free
                    </Link>
                  )
                ) : (
                  <button
                    onClick={() => subscribe(t)}
                    disabled={isBusy || priceMissing || alreadyOnThis}
                    className={t.highlight ? "wofy-btn-primary mt-auto justify-center" : "wofy-btn-ghost mt-auto justify-center border"}
                    style={t.highlight ? {} : { borderColor: "var(--wofy-line)" }}
                    data-testid={`pricing-subscribe-${t.id}`}
                  >
                    {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {alreadyOnThis ? "Current plan"
                      : priceMissing ? "Coming soon"
                      : user ? "Subscribe" : "Sign in to subscribe"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-center text-xs mt-8" style={{ color: "var(--wofy-text-soft)" }}>
        Secure payments powered by Stripe · Cancel any time from your dashboard.
      </p>
    </div>
  );
}
